import type { Meeting, MeetingTypeType } from "@repo/types";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import { SquareArrowOutUpRight, EyeIcon, MoreHorizontal } from "lucide-react";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import { AgendaSummaryItem } from "../agendas/AgendaSummaryItem";
import { RoleProtected } from "../common/RoleProtected";
import {
  createCreatedAtColumn,
  createCreatedByColumn,
  createDescriptionColumn,
  createIdColumn,
  createNameColumn,
  createNestedTagColumn,
  createStatusColumn,
  createTagColumn,
  createTeamColumn,
  createUpdatedAtColumn,
  createUpdatedByColumn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { DATE_FORMAT } from "@/lib/constants";
import { useUserStore } from "@/stores/userStore";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";
import { MeetingTypeName } from "./MeetingTypeName";
import { configRepo } from "@/repositories/configRepo";
import { cn } from "@repo/shared/utils";
import { firstValueFrom } from "rxjs";
import { useDocumentList } from "@/hooks/queryHooks/documentHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";

export const createMeetingAgendaColumn = (
  tenantId: string
): ColumnDef<Meeting> => ({
  id: "agenda",
  accessorKey: "agenda",
  accessorFn: (row) =>
    `${!row.agendaId ? "No Agenda Attached" : row.agenda?.name ?? "..."}`,
  sortingFn: (a, b) => {
    return (a.original.agenda?.name ?? "") > (b.original.agenda?.name ?? "")
      ? 1
      : -1;
  },

  meta: {
    label: "Agenda",
    filterVariant: "text",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Agenda" />
  ),
  cell: ({ row }) => {
    const meeting = row.original;

    return (
      <div>
        <AgendaSummaryItem tenantId={tenantId} agendaId={meeting.agendaId} />
      </div>
    );
  },
});

export const MeetingMeetingDateColumn: ColumnDef<Meeting> = {
  id: "meetingDate",
  accessorKey: "startTime",
  filterFn: "date",
  meta: {
    label: "Meeting Date",
    filterVariant: "date",
  },
  sortingFn: (a, b) => {
    const timeA = a.original.startTime;
    const timeB = b.original.startTime;
    return timeA < timeB ? -1 : timeA > timeB ? 1 : 0;
  },

  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Meeting Date" />
  ),
  cell: ({ row }) => {
    return <div>{format(row.original.startTime, DATE_FORMAT)}</div>;
  },
};

export const MeetingMeetingTimeColumn: ColumnDef<Meeting> = {
  id: "meetingTime",
  meta: {
    label: "Meeting Time",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Meeting Time" noSort />
  ),
  cell: ({ row }) => {
    return (
      <div>{`${format(row.original.startTime, "h:mm b")} - ${format(
        row.original.endTime,
        "h:mm b"
      )}`}</div>
    );
  },
};

export const MeetingTypeColumn: ColumnDef<Meeting> = {
  accessorKey: "meetingType",
  filterFn: "async",
  meta: {
    label: "Meeting Type",
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      // cast inputs
      const state = rawState as {
        meetingTypes?: Record<string, MeetingTypeType>;
        promise?: Promise<void>;
      };
      const filterValue = (rawFilterValue ?? "") as string;

      // wait for config to load (only load once)
      if (!state.promise) {
        state.promise = (async () => {
          // load config
          const { meetingTypes } = await firstValueFrom(
            configRepo.get("meetingTypes")
          );

          // map meeting type values to objects
          state.meetingTypes = meetingTypes.reduce(
            (map, type) => ((map[type.value] = type), map),
            Object.create(null)
          );
        })();
      }
      await state.promise;

      // test for match
      if (!filterValue) return true;

      const label = state.meetingTypes?.[row.original.meetingType]?.label;
      return label?.toLowerCase().includes(filterValue.toLowerCase()) ?? false;
    },
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Meeting Type" />
  ),
  cell: ({ row }) => <MeetingTypeName type={row.original.meetingType} />,
};

export const MeetingEditedColumn: ColumnDef<Meeting> = {
  id: "editedSincePublished",
  accessorKey: "editedSincePublished",
  meta: {
    label: "Unpublished Edits",
    filterVariant: "customSelect",
    customSelectOptions: [
      { label: "Has Unpublished Edits", value: "edits" },
      { label: "No Unpublished Edit", value: "noEdits" },
    ],
  },
  filterFn: (row, _, value) => {
    const publishedAt = row.original.agendaPublishedAt;
    if (!publishedAt) {
      return value === "edits";
    }
    if (value === "edits") {
      return publishedAt < row.original.updatedAt;
    } else if (value === "noEdits") {
      return publishedAt >= row.original.updatedAt;
    }
    return true;
  },
  sortingFn: (a, b) => {
    const publishedAtA = a.original.agendaPublishedAt ?? a.original.updatedAt;
    const publishedAtB = b.original.agendaPublishedAt ?? b.original.updatedAt;

    const isEditedA = publishedAtA < a.original.createdAt;
    const isEditedB = publishedAtB < b.original.createdAt;
    return isEditedA && !isEditedB ? -1 : !isEditedA && isEditedB ? 1 : 0;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Unpublished Edits" />
  ),
  cell: ({ row }) => {
    const isPublished =
      row.original.agendaPublishedAt ?? row.original.publishedAt;

    if (!isPublished) {
      return "Unpublished Edits";
    }

    const publishedAt =
      row.original.agendaPublishedAt ?? row.original.updatedAt;
    const updatedAt = row.original.updatedAt;

    return <div>{publishedAt < updatedAt ? "Unpublished Edits" : ""}</div>;
  },
};

export const useColumns = ({
  tenantId,
  handleQuickEdit,
  handleEdit,
  handleReoccurring,
  handlePublishRequest,
  handleRePublishRequest,
  handleArchive,
  handleCopyPdf,
  handleDownloadPdf,
  handlePreview,
}: {
  tenantId: string;
  handleQuickEdit: (id: string) => void;
  handleEdit: (id: string) => void;
  handleReoccurring: (id: string) => void;
  handlePublishRequest: (id: string) => void;
  handleRePublishRequest: (id: string) => void;
  handleArchive: (id: string) => void;
  handleCopyPdf: (id: string) => void;
  handleDownloadPdf: (id: string) => void;
  handlePreview: (id: string) => void;
}) => {
  handleQuickEdit = useFutureCallback(handleQuickEdit);
  handleEdit = useFutureCallback(handleEdit);
  handleReoccurring = useFutureCallback(handleReoccurring);
  handlePublishRequest = useFutureCallback(handlePublishRequest);
  handleRePublishRequest = useFutureCallback(handleRePublishRequest);
  handleArchive = useFutureCallback(handleArchive);
  handleCopyPdf = useFutureCallback(handleCopyPdf);
  handleDownloadPdf = useFutureCallback(handleDownloadPdf);
  handlePreview = useFutureCallback(handlePreview);

  const { data: documents } = useDocumentList(
    tenantId,
    useActiveTeamIdOrOrg() ?? ""
  );
  const meetingsWithMinutes = useMemo(
    () =>
      documents?.reduce(
        (map, doc) => {
          if (doc.minutesData?.meetingId) map[doc.minutesData.meetingId] = true;
          return map;
        },
        Object.create(null) as Record<string, true>
      ),
    [documents]
  );

  const col: ColumnDef<Meeting>[] = useMemo(() => {
    return [
      {
        id: "actions",
        header: "",
        cell: function Cell({ row }) {
          const team = useUserStore((s) => s.activeTeam);
          const isFullOrg = !team || team === "org";

          const meeting = row.original;
          return (
            <div className="flex flex-row align-middle gap-1 justify-start">
              <Button
                variant="ghost"
                tooltip={isFullOrg ? "Pick a team first" : "Open the meeting"}
                className={cn(
                  "h-8 w-8 p-0",
                  isFullOrg && "opacity-50 cursor-not-allowed"
                )}
                disabled={isFullOrg}
                onClick={() => handleEdit(meeting.id)}
              >
                <SquareArrowOutUpRight />
              </Button>
              {meeting.agendaPublishedAt && (
                <Button
                  variant="ghost"
                  tooltip="View the last publically accessible agenda"
                  className="h-8 w-8 p-0"
                  onClick={() => handlePreview(meeting.id)}
                >
                  <EyeIcon />
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <RoleProtected minRole="edit">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={
                        isFullOrg
                          ? "Pick a team first"
                          : "Edit meeting in a side panel"
                      }
                      onClick={() => handleQuickEdit(meeting.id)}
                      className="cursor-pointer"
                    >
                      Quick Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={
                        isFullOrg
                          ? "Pick a team first"
                          : "Edit the meeting in a new window"
                      }
                      onClick={() => handleEdit(meeting.id)}
                      className="cursor-pointer"
                    >
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={
                        isFullOrg
                          ? "Pick a team first"
                          : "Create scheduled copies of meeting"
                      }
                      onClick={() => handleReoccurring(meeting.id)}
                    >
                      Reoccurring Copies
                    </DropdownMenuItem>
                    {meeting.status !== "public" && (
                      <DropdownMenuItem
                        disabled={meetingsWithMinutes?.[meeting.id]}
                        tooltip={
                          meetingsWithMinutes?.[meeting.id]
                            ? "Cannot re-publish a past meeting"
                            : "Publish meeting to the public"
                        }
                        onClick={() => handlePublishRequest(meeting.id)}
                        className="cursor-pointer"
                      >
                        Publish
                      </DropdownMenuItem>
                    )}
                    {meeting.status === "public" && (
                      <DropdownMenuItem
                        disabled={meetingsWithMinutes?.[meeting.id]}
                        tooltip={
                          meetingsWithMinutes?.[meeting.id]
                            ? "Cannot re-publish a past meeting"
                            : "Re-publish meeting to the public"
                        }
                        onClick={() => handleRePublishRequest(meeting.id)}
                        className="cursor-pointer"
                      >
                        Re-publish
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      tooltip="Delete the current meeting"
                      onClick={() => handleArchive(meeting.id)}
                      className="cursor-pointer"
                    >
                      Delete
                    </DropdownMenuItem>
                    {meeting.agendaPublishedAt && (
                      <DropdownMenuItem
                        tooltip="Copy the url to the publicly accessible agenda"
                        onClick={() => handleCopyPdf(meeting.id)}
                        className="cursor-pointer"
                      >
                        Copy Agenda URL
                      </DropdownMenuItem>
                    )}
                    {meeting.agendaPublishedAt && (
                      <DropdownMenuItem
                        tooltip="Download the last publicly accessible agenda"
                        onClick={() => handleDownloadPdf(meeting.id)}
                        className="cursor-pointer"
                      >
                        Download Agenda
                      </DropdownMenuItem>
                    )}
                  </RoleProtected>
                  {meeting.agendaPublishedAt && (
                    <DropdownMenuItem
                      tooltip="View the last publicly accessible agenda"
                      onClick={() => handlePreview(meeting.id)}
                      className="cursor-pointer"
                    >
                      View Agenda
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigator.clipboard.writeText(meeting.id)}
                    className="cursor-pointer"
                  >
                    Copy meeting ID
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createNameColumn(),
      createTagColumn("Meeting Tags"),
      createNestedTagColumn("Agenda Item Tags"),
      createDescriptionColumn(),
      createStatusColumn(),
      MeetingTypeColumn,
      MeetingEditedColumn,
      createMeetingAgendaColumn(tenantId),
      MeetingMeetingDateColumn,
      MeetingMeetingTimeColumn,
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
    ];
  }, [
    tenantId,
    handleQuickEdit,
    handleEdit,
    handleReoccurring,
    handlePublishRequest,
    handleRePublishRequest,
    handleArchive,
    handleCopyPdf,
    handleDownloadPdf,
    handlePreview,
    meetingsWithMinutes,
  ]);
  return col;
};

export const layoutDefaults: TableLayoutState = {
  sorting: [{ id: "meetingDate", desc: false }],
  columnFilters: [
    {
      id: "meetingDate",
      value: [format(new Date(), "yyyy-MM-dd"), ""],
    },
  ],
  columnVisibility: {
    id: false,
    meetingType: false,
    createdBy: false,
    updatedAt: false,
    updatedBy: false,
    agenda: false,
    teamId: false,
  },
};
